<div class="d-flex flex-wrap justify-content-between align-items-center mb-4">
  <div>
    <h5 class="mb-1 fw-bold">Factory Go-Down Stock</h5>
    <span class="sub-text">Finished goods stock at factory level</span>
  </div>

  <div class="d-flex gap-2 align-items-center flex-wrap">
    <ng-select
      placeholder="All Factories"
      [items]="factoryList"
      [(ngModel)]="selectedFactory"
      bindValue="id"
      bindLabel="name"
      [clearable]="true"
      [searchable]="false"
      (ngModelChange)="onFactoryChange()"
      style="min-width: 200px" />
    <button
      class="btn"
      [class.btn-primary]="showBatchView"
      [class.btn-soft-primary]="!showBatchView"
      (click)="toggleBatchView()">
      <i class="bi bi-layers"></i>
      {{ showBatchView ? 'Hide Batches' : 'Show Batches' }}
    </button>
    <button class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2" (click)="openExportModal()">
      <i class="bi bi-download"></i>
      Export Report
    </button>
  </div>
</div>

<div class="alert alert-info d-flex align-items-start mb-3" role="alert">
  <i class="bi bi-info-circle-fill me-2 mt-1"></i>
  <div>
    <strong>Stock here moves through production and transfers.</strong>
    Where a physical count disagrees with the system, correct it with Adjust on a batch row under Show Batches. An
    adjustment cannot be edited or deleted: a wrong one is undone by posting its opposite, and both stay in the ledger.
    A batch sitting at zero does not appear here, so adjust it from the Production Entries tab instead.
  </div>
</div>

<!-- Export Modal -->
<ng-template #exportModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold">Export Factory Stock</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    @if (exportError) {
      <app-error-alert [error]="exportError" class="mb-3"></app-error-alert>
    }
    <div>
      <label class="form-label">Factory</label>
      <ng-select
        [items]="factoryList"
        bindLabel="name"
        bindValue="id"
        placeholder="All Factories"
        [(ngModel)]="exportFactory"
        [clearable]="true"></ng-select>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary btn-sm" (click)="modal.dismiss()" [disabled]="isExporting">
      Cancel
    </button>
    <button
      type="button"
      class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
      (click)="onExportSubmit(modal)"
      [disabled]="isExporting">
      @if (isExporting) {
        <span class="spinner-border spinner-border-sm"></span>
        Exporting...
      } @else {
        <i class="bi bi-download"></i>
        Export
      }
    </button>
  </div>
</ng-template>

@if (isDataLoading) {
  <app-loading-spinner></app-loading-spinner>
} @else {
  @if (!showBatchView) {
    @if (stockItems.length === 0) {
      <app-info-alert>
        No factory stock found.
        @if (!selectedFactory) {
          Select a specific factory or add production entries to populate the go-down.
        }
      </app-info-alert>
    } @else {
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="bg-light">
            <tr>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Product</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Packaging</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Factory</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Available</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Total Stock Cost</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Last Updated</th>
            </tr>
          </thead>
          <tbody>
            @for (item of stockItems; track item.id) {
              <tr>
                <td class="py-3 fw-semibold">{{ item.product_name }}</td>
                <td class="py-3 text-muted small">
                  {{ item.packing_size }} {{ item.unit }} / {{ item.packaging_type }}
                </td>
                <td class="py-3">
                  <span class="badge app-badge badge-soft-primary">{{ item.factory_name | titlecase }}</span>
                </td>
                <td class="py-3 text-center">
                  <span
                    class="fw-bold font-monospace"
                    [class.text-success]="+item.boxes_available > 0"
                    [class.text-danger]="+item.boxes_available <= 0">
                    {{ item.boxes_available }} boxes
                  </span>
                  <div class="text-muted small font-monospace">({{ item.units_available }} nos)</div>
                </td>
                <td class="py-3 text-center text-muted small">
                  {{ item.total_stock_cost | currency: 'INR' }}
                </td>
                <td class="py-3 text-center text-muted small">
                  {{ item.updated_at | date: 'mediumDate' }}
                </td>
              </tr>
            }
          </tbody>
          @if (totalStockValue != null) {
            <tfoot>
              <tr class="table-light border-top">
                <td colspan="4" class="py-2 pe-3 text-end text-muted small fw-semibold">
                  Total Stock Value (all pages)
                </td>
                <td class="py-2 text-center fw-bold small">
                  {{ totalStockValue | currency: 'INR' : 'symbol' : '1.2-2' }}
                </td>
                <td></td>
              </tr>
            </tfoot>
          }
        </table>
      </div>

      @if (stockItems.length > 0) {
        <div class="mt-4">
          <app-pagination
            [currentPage]="currentPage"
            [totalCount]="totalCount"
            [pageSize]="10"
            [hasNextPage]="hasNextPage"
            [hasPreviousPage]="hasPreviousPage"
            (pageChange)="onPageChange($event)"></app-pagination>
        </div>
      }
    }
  } @else {
    @if (groupedBatchStock.length === 0) {
      <app-info-alert>
        No batch stock found.
        @if (!selectedFactory) {
          Select a specific factory to view batch-level stock.
        }
      </app-info-alert>
    } @else {
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="bg-light">
            <tr>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Product</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Packaging</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Batch No.</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">MFG Date</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">EXP Date</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Available</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Total Stock Cost</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Factory</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (group of groupedBatchStock; track group.product_name) {
              @for (row of group.rows; track $index; let isFirst = $first) {
                <tr>
                  @if (isFirst) {
                    <td class="py-3 fw-semibold align-middle" [attr.rowspan]="group.rows.length + 1">
                      {{ group.product_name }}
                    </td>
                  }
                  <td class="py-3 text-muted small">{{ row.packaging }}</td>
                  <td class="py-3 font-monospace small fw-semibold">{{ row.batch_number }}</td>
                  <td class="py-3 text-center text-muted small">{{ row.mfg_date }}</td>
                  <td class="py-3 text-center text-muted small">{{ row.exp_date }}</td>
                  <td class="py-3 text-center">
                    <span
                      class="fw-bold font-monospace"
                      [class.text-success]="+row.boxes_available > 0"
                      [class.text-danger]="+row.boxes_available <= 0">
                      {{ row.boxes_available }} boxes
                    </span>
                    <div class="text-muted small font-monospace">({{ row.units_available }} nos)</div>
                  </td>
                  <td class="py-3 text-center text-muted small">
                    {{ row.total_stock_cost | currency: 'INR' }}
                  </td>
                  <td class="py-3">
                    <span class="badge app-badge badge-soft-primary">{{ row.factory_name | titlecase }}</span>
                  </td>
                  <td class="py-3 text-center text-nowrap">
                    <button
                      type="button"
                      class="btn btn-soft-primary btn-sm me-1"
                      title="Adjust this batch's stock"
                      (click)="openAdjustModal(row.source, adjustModal)">
                      <i class="bi bi-sliders"></i>
                    </button>
                    <button
                      type="button"
                      class="btn btn-soft-primary btn-sm"
                      title="Adjustment history for this batch"
                      (click)="openHistoryModal(row.source, historyModal)">
                      <i class="bi bi-clock-history"></i>
                    </button>
                  </td>
                </tr>
              }
              <!-- Total cost row for this product -->
              <tr class="table-light border-top">
                <td colspan="5" class="py-2 pe-3 text-end text-muted small fw-semibold">Total Cost</td>
                <td class="py-2 text-center fw-bold small">{{ group.total_cost | currency: 'INR' }}</td>
                <td></td>
                <td></td>
              </tr>
            }
          </tbody>
          @if (totalStockValue != null) {
            <tfoot>
              <tr class="table-light border-top">
                <td colspan="6" class="py-2 pe-3 text-end text-muted small fw-semibold">
                  Total Stock Value (all pages)
                </td>
                <td class="py-2 text-center fw-bold small">
                  {{ totalStockValue | currency: 'INR' : 'symbol' : '1.2-2' }}
                </td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          }
        </table>
      </div>

      @if (groupedBatchStock.length > 0) {
        <div class="mt-4">
          <app-pagination
            [currentPage]="currentPage"
            [totalCount]="totalCount"
            [pageSize]="10"
            [hasNextPage]="hasNextPage"
            [hasPreviousPage]="hasPreviousPage"
            (pageChange)="onPageChange($event)"></app-pagination>
        </div>
      }
    }
  }
}

<!-- Adjust batch stock -->
<ng-template #adjustModal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold">Adjust Factory Stock</h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="onAdjustCancelled()"></button>
  </div>

  @if (adjustContext) {
    <app-product-stock-adjustment-form
      [context]="adjustContext"
      (saved)="onAdjustmentSaved()"
      (cancelled)="onAdjustCancelled()"></app-product-stock-adjustment-form>
  }
</ng-template>

<!-- Adjustment history for one batch -->
<ng-template #historyModal let-modal>
  <div class="modal-header">
    <div>
      <h5 class="modal-title fw-bold">Adjustment History</h5>
      @if (historyBatch) {
        <span class="text-muted small">
          Batch {{ historyBatch.batch_number }} &middot; {{ historyBatch.product_name }} &middot;
          {{ historyBatch.factory_name }}
        </span>
      }
    </div>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>

  <div class="modal-body p-4">
    @if (historyBatch) {
      <app-product-stock-adjustments
        scope="factory"
        [productionEntry]="historyBatch.production_entry"
        [refreshToken]="historyRefreshToken"></app-product-stock-adjustments>
    }
  </div>
</ng-template>
